import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CalendarDays, Users, FilterX, Download } from 'lucide-react';

// Monthly-quota rule (Feb 2026): every employee gets 4 paid leaves per month.
// Anything beyond becomes LOP (payroll deducts per-day salary automatically).

export default function LeaveDetailsPage() {
  const { user } = useAuth();
  const nowY = new Date().getFullYear();
  const nowM = new Date().toISOString().slice(0, 7);
  const [year, setYear] = useState(nowY);
  const [month, setMonth] = useState('');
  const [empFilter, setEmpFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [monthlyQuota, setMonthlyQuota] = useState(4);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const canView = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role) || user?.job_role === 'H.R';

  const fetchData = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { year };
      if (month) params.month = month;
      if (empFilter && empFilter !== 'all') params.employee_id = empFilter;
      const [res, empRes] = await Promise.all([
        api.get('/leaves/details-summary', { params }),
        employees.length === 0 ? api.get('/employees') : Promise.resolve({ data: employees }),
      ]);
      setRows(res.data.rows || []);
      setMonthlyQuota(res.data.monthly_quota || 4);
      if (employees.length === 0) setEmployees(empRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, month, empFilter, canView, employees]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalDaysTaken = useMemo(() => rows.reduce((s, r) => s + (r.days_taken || 0), 0), [rows]);
  const totalLop = useMemo(() => rows.reduce((s, r) => s + (r.lop_days || 0), 0), [rows]);
  const uniqEmployees = useMemo(() => new Set(rows.map(r => r.employee_id)).size, [rows]);

  const handleReset = () => { setYear(nowY); setMonth(''); setEmpFilter('all'); };

  const handleExportCSV = () => {
    const header = ['Employee Code', 'Employee', 'Branch', 'Job Role', 'Month', 'Monthly Quota', 'Days Taken', 'Balance', 'LOP Days', 'Applied Dates'];
    const csvRows = [header.join(',')];
    for (const r of rows) {
      const applied = (r.applied_entries || [])
        .map(e => `${e.start_date}${e.start_date === e.end_date ? '' : `..${e.end_date}`}${e.is_half_day ? ' (half)' : ''}`)
        .join(' | ');
      csvRows.push([
        r.employee_code || '',
        `"${(r.employee_name || '').replace(/"/g, '""')}"`,
        `"${(r.branch_name || '').replace(/"/g, '""')}"`,
        r.job_role || '',
        r.month,
        r.monthly_quota,
        r.days_taken,
        r.balance_remaining,
        r.lop_days,
        `"${applied}"`,
      ].join(','));
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `leave_details_${year}${month ? `_${month}` : ''}.csv`;
    a.click();
  };

  if (!canView) {
    return <div className="p-6 text-neutral-600" data-testid="leave-details-forbidden">You don&apos;t have permission to view leave details.</div>;
  }

  return (
    <div className="space-y-6" data-testid="leave-details-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-neutral-700" /> Leave Details
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every employee gets {monthlyQuota} paid leaves per month. Extra days are auto-deducted as Loss of Pay.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} className="rounded-sm" data-testid="leave-details-reset">
            <FilterX className="h-4 w-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleExportCSV} className="rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="leave-details-export">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="leave-details-filter-grid">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Year</Label>
              <Input type="number" min="2020" max="2100" value={year} onChange={e => setYear(Number(e.target.value) || nowY)}
                     className="rounded-sm mt-1" data-testid="leave-details-year-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Month (optional)</Label>
              <Input type="month" value={month} placeholder={nowM} onChange={e => setMonth(e.target.value)}
                     className="rounded-sm mt-1" data-testid="leave-details-month-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Employee</Label>
              <Select value={empFilter} onValueChange={setEmpFilter}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="leave-details-employee-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><p className="text-xs text-neutral-500 uppercase tracking-wider">Employees with Leaves</p><p className="text-2xl font-semibold mt-1" data-testid="leave-stat-employees">{uniqEmployees}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-neutral-500 uppercase tracking-wider">Total Days Taken</p><p className="text-2xl font-semibold mt-1" data-testid="leave-stat-taken">{totalDaysTaken.toFixed(1)}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-neutral-500 uppercase tracking-wider">Total LOP Days</p><p className={`text-2xl font-semibold mt-1 ${totalLop > 0 ? 'text-red-600' : 'text-neutral-900'}`} data-testid="leave-stat-lop">{totalLop.toFixed(1)}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-neutral-500 uppercase tracking-wider">Monthly Quota</p><p className="text-2xl font-semibold mt-1">{monthlyQuota}<span className="text-sm font-normal text-neutral-500"> /month</span></p></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Per-Employee, Per-Month Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <TableHead>Employee</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Quota</TableHead>
                  <TableHead className="text-right">Taken</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">LOP</TableHead>
                  <TableHead>Applied Dates</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`skel-${i}`}><TableCell colSpan={8}><div className="h-10 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-neutral-500"><Users className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No leave data for the selected filters.</TableCell></TableRow>
                ) : rows.map(r => (
                  <TableRow key={`${r.employee_id}-${r.month}`} data-testid={`row-${r.employee_id}-${r.month}`}>
                    <TableCell>
                      <div className="font-medium text-neutral-900 text-sm">{r.employee_name}</div>
                      <div className="text-xs text-neutral-500">{r.employee_code} · {r.job_role}</div>
                    </TableCell>
                    <TableCell className="text-sm text-neutral-600">{r.branch_name || '—'}</TableCell>
                    <TableCell className="text-sm text-neutral-800 font-mono">{r.month}</TableCell>
                    <TableCell className="text-right text-sm">{r.monthly_quota}</TableCell>
                    <TableCell className="text-right text-sm font-medium">{r.days_taken}</TableCell>
                    <TableCell className={`text-right text-sm font-semibold ${r.balance_remaining <= 0 ? 'text-neutral-500' : 'text-emerald-700'}`}>{r.balance_remaining}</TableCell>
                    <TableCell className="text-right text-sm">
                      {r.lop_days > 0
                        ? <Badge className="bg-red-100 text-red-700 border-red-200 text-[11px]">{r.lop_days}</Badge>
                        : <span className="text-neutral-400">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-neutral-600">
                      {(r.applied_entries || []).length === 0 ? '—' : (r.applied_entries || []).map((e) => (
                        <div key={e.id} className="whitespace-nowrap">
                          {e.start_date}{e.start_date !== e.end_date ? ` → ${e.end_date}` : ''}
                          {e.is_half_day && <span className="ml-1 text-amber-700">(half · {e.half_day_session})</span>}
                          <span className="ml-2 text-neutral-400">· {e.days}d</span>
                        </div>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
